import { createTheme, type Theme } from '@mui/material/styles';

/**
 * MUI theme with LITERAL scheme colors — MUI needs parseable colors for its
 * contrast/alpha math (CSS `var()` strings throw). `cssVariables` with
 * `colorSchemeSelector: 'data-mui-color-scheme'` makes MUI emit both schemes
 * and switch on the exact attribute our pre-hydration script stamps, so MUI's
 * surfaces and text always resolve from the SAME scheme (no contrast mismatch,
 * no flash). tokens.css mirrors these values for non-MUI CSS off the same
 * attribute — one visual source of truth, two consumers.
 *
 * Palette: brand gold on pure white / pure black. `primary` is the gold itself
 * with near-black ink as its contrast text, which is what makes a filled button
 * legible; anything that renders gold as *text* uses GOLD_INK instead — see
 * tokens.css for the contrast arithmetic.
 */

/** rgb(212 160 23) and rgb(199 139 45), the two brand values. */
export const GOLD = '#d4a017';
export const GOLD_DEEP = '#c78b2d';
/** Same hue, darkened until small text clears 4.5:1 on white (5.4:1). */
export const GOLD_INK = '#8a6410';
/** Same hue, lightened for text on black (11.8:1). */
export const GOLD_INK_DARK = '#e8bd52';
/** Ink that sits on a gold fill (8.8:1). */
export const ON_GOLD = '#241a02';

const LIGHT = {
  primary: { main: GOLD, dark: GOLD_DEEP, light: '#e2b854', contrastText: ON_GOLD },
  secondary: { main: '#0f766e', contrastText: '#ffffff' },
  success: { main: '#16a34a', contrastText: '#ffffff' },
  warning: { main: '#b45309', contrastText: '#ffffff' },
  error: { main: '#dc2626', contrastText: '#ffffff' },
  info: { main: '#0b5fff', contrastText: '#ffffff' },
  background: { default: '#ffffff', paper: '#ffffff' },
  text: { primary: '#18181b', secondary: '#5f5f6a' },
  divider: '#e4e4e7',
};

const DARK = {
  primary: { main: '#e0ae2b', dark: GOLD_DEEP, light: '#eecb6d', contrastText: '#1a1200' },
  secondary: { main: '#2dd4bf', contrastText: '#042f2a' },
  success: { main: '#4ade80', contrastText: '#052012' },
  warning: { main: '#fbbf24', contrastText: '#231a00' },
  error: { main: '#f87171', contrastText: '#2a0605' },
  info: { main: '#5c8dff', contrastText: '#061024' },
  background: { default: '#000000', paper: '#000000' },
  text: { primary: '#f4f4f5', secondary: '#a1a1ab' },
  divider: '#2a2a30',
};

/** Soft, layered elevation instead of MUI's default heavy umbras. */
const POP_SHADOW = '0 4px 8px -2px rgba(24, 24, 27, 0.06), 0 18px 44px -8px rgba(24, 24, 27, 0.16)';

/**
 * Gold as *text* must resolve per scheme, and MUI's `sx` cannot branch on the
 * color-scheme attribute the way a CSS variable can — so text-role gold reads
 * from tokens.css, which already flips it.
 */
const GOLD_TEXT = 'var(--fms-color-gold-ink)';

export function createFmsTheme(direction: 'ltr' | 'rtl' = 'ltr'): Theme {
  return createTheme({
    direction,
    cssVariables: { colorSchemeSelector: 'data-mui-color-scheme' },
    colorSchemes: {
      light: { palette: LIGHT },
      dark: { palette: DARK },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: 'var(--fms-font-sans)',
      button: { textTransform: 'none', fontWeight: 600 },
      h1: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.01em' },
      h3: { fontSize: '1.125rem', fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      overline: { fontWeight: 700 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { minHeight: 44, borderRadius: 9 }, // WCAG 2.5.8 target size
          sizeSmall: { minHeight: 36 },
          // Outlined and text buttons render their label in the palette colour,
          // which for `primary` is the brand gold — too light for text on white.
          // Both re-point at the ink shade and take the brand gold on the border
          // and the hover wash, where no contrast floor applies.
          outlinedPrimary: {
            color: GOLD_TEXT,
            borderColor: 'var(--fms-color-gold)',
            '&:hover': {
              borderColor: 'var(--fms-color-gold-deep)',
              backgroundColor: 'var(--fms-color-gold-tint)',
            },
          },
          textPrimary: {
            color: GOLD_TEXT,
            '&:hover': { backgroundColor: 'var(--fms-color-gold-tint)' },
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: { root: { color: GOLD_TEXT, fontWeight: 500 } },
      },
      MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 9,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--fms-color-gold-deep)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--fms-color-gold)',
              borderWidth: 2,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { '&.Mui-focused': { color: GOLD_TEXT } } },
      },
      MuiFormHelperText: { styleOverrides: { root: { marginLeft: 0 } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: '1px solid var(--fms-color-border)',
            boxShadow: 'var(--fms-shadow-1)',
          },
        },
      },
      MuiDialog: {
        defaultProps: { maxWidth: 'sm', fullWidth: true },
        styleOverrides: { paper: { borderRadius: 14 } },
      },
      // On a pure-white/pure-black canvas an unbordered popover has no edge at
      // all — the elevation shadow alone stops reading as a separate surface.
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: POP_SHADOW,
            borderRadius: 10,
            border: '1px solid var(--fms-color-border)',
            backgroundColor: 'var(--fms-color-surface)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: POP_SHADOW,
            borderRadius: 10,
            border: '1px solid var(--fms-color-border)',
            backgroundColor: 'var(--fms-color-surface)',
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 400 },
        styleOverrides: { tooltip: { borderRadius: 6, fontSize: 12 } },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 44,
            '&.Mui-selected': { color: GOLD_TEXT },
          },
        },
      },
      MuiTabs: {
        styleOverrides: { indicator: { backgroundColor: 'var(--fms-color-gold)' } },
      },
      MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
      MuiListItemButton: { styleOverrides: { root: { borderRadius: 8 } } },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 700, whiteSpace: 'nowrap' },
        },
      },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiSkeleton: { defaultProps: { animation: 'wave' } },
      MuiCheckbox: { styleOverrides: { root: { '&.Mui-checked': { color: GOLD_TEXT } } } },
      MuiRadio: { styleOverrides: { root: { '&.Mui-checked': { color: GOLD_TEXT } } } },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': { color: 'var(--fms-color-gold)' },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--fms-color-gold)' },
          },
        },
      },
    },
  });
}
